import React, { useEffect, useMemo, useState } from 'react';
import { Download, FileSpreadsheet, CheckCircle2, AlertCircle, Package, Sparkles, Info, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { datevAPI, downloadFile } from '../../services/api';
import { formatEuro } from '../../services/serviceCatalog';
import LoadingSpinner from '../Common/LoadingSpinner';
import './DatevExport.css';

const MONTHS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

const STATUS_CLASS = {
  paid: 'status-badge status-paid',
  sent: 'status-badge status-sent',
  overdue: 'status-badge status-overdue',
  draft: 'status-badge status-draft',
};

const DatevExport = () => {
  const today = useMemo(() => new Date(), []);
  const [kind, setKind] = useState('month');
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [quarter, setQuarter] = useState(Math.floor(today.getMonth() / 3) + 1);
  const [startDate, setStartDate] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10)
  );
  const [endDate, setEndDate] = useState(today.toISOString().slice(0, 10));
  const [includeUnpaid, setIncludeUnpaid] = useState(false);

  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);

  // Full-report state (separate period, defaults to "auto" / empty)
  const [reportFrom, setReportFrom] = useState('');
  const [reportTo, setReportTo] = useState('');
  const [reportIncludeBrief, setReportIncludeBrief] = useState(true);
  const [reportPreview, setReportPreview] = useState(null);
  const [reportPreviewLoading, setReportPreviewLoading] = useState(false);
  const [reportDownloading, setReportDownloading] = useState(false);

  const buildParams = () => {
    const p = { kind, include_unpaid: includeUnpaid };
    if (kind === 'month') {
      p.year = year; p.month = month;
    } else if (kind === 'quarter') {
      p.year = year; p.quarter = quarter;
    } else if (kind === 'year') {
      p.year = year;
    } else if (kind === 'custom') {
      p.start_date = startDate; p.end_date = endDate;
    }
    return p;
  };

  const loadPreview = async () => {
    try {
      setLoading(true);
      const { data } = await datevAPI.preview(buildParams());
      setPreview(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Vorschau fehlgeschlagen');
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, year, month, quarter, startDate, endDate, includeUnpaid]);

  const handleDownload = async (format) => {
    try {
      setDownloading(format);
      const res = await datevAPI.download({ ...buildParams(), format });
      const fname =
        res.headers['content-disposition']?.match(/filename="?([^";]+)"?/)?.[1] ||
        `${format === 'datev' ? 'DATEV' : 'Rechnungen'}.csv`;
      downloadFile(res.data, fname);
      toast.success('Export erstellt');
    } catch (err) {
      toast.error('Export fehlgeschlagen');
    } finally {
      setDownloading(null);
    }
  };

  const buildReportParams = () => {
    const p = {};
    if (reportFrom) p.start_date = reportFrom;
    if (reportTo) p.end_date = reportTo;
    return p;
  };

  const loadReportPreview = async () => {
    try {
      setReportPreviewLoading(true);
      const { data } = await datevAPI.fullReportPreview(buildReportParams());
      setReportPreview(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Vorschau fehlgeschlagen');
      setReportPreview(null);
    } finally {
      setReportPreviewLoading(false);
    }
  };

  const downloadFullReport = async () => {
    try {
      setReportDownloading(true);
      const tid = toast.loading(reportIncludeBrief ? 'Bericht wird mit KI erstellt…' : 'Bericht wird erstellt…');
      const params = { ...buildReportParams(), include_brief: reportIncludeBrief };
      const res = await datevAPI.fullReport(params);
      const fname =
        res.headers['content-disposition']?.match(/filename="?([^";]+)"?/)?.[1] ||
        'Finanzbericht.zip';
      downloadFile(res.data, fname);
      toast.success('ZIP heruntergeladen', { id: tid });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bericht fehlgeschlagen');
    } finally {
      setReportDownloading(false);
    }
  };

  useEffect(() => {
    loadReportPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportFrom, reportTo]);

  const years = [];
  for (let y = today.getFullYear() + 1; y >= today.getFullYear() - 5; y--) years.push(y);

  return (
    <div>
      <div className="content-header">
        <div>
          <h1>Steuer &amp; Export</h1>
          <p>Buchhaltungsexport für Steuerberater · DATEV-Buchungsstapel &amp; einfache CSV</p>
        </div>
      </div>

      <div className="content-body">
        <div className="card mb-4" style={{ borderLeft: '4px solid #6366f1' }}>
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Package size={18} style={{ color: '#6366f1' }} />
              <h2 style={{ margin: 0 }}>Vollständiger Steuerberater-Bericht</h2>
            </div>
            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              ZIP · Rechnungen + Ausgaben + UStVA + KI-Brief
            </span>
          </div>
          <div className="card-body">
            <p style={{ marginBottom: '1rem', color: '#4b5563', fontSize: '0.9rem' }}>
              Bündelt alles, was der Steuerberater braucht: DATEV-Buchungsstapel (Rechnungen + Ausgaben),
              Monatsübersicht, UStVA-Kennzahlen, Kunden- und Kategorien-Aufschlüsselung sowie einen
              KI-generierten Brief mit Auffälligkeiten und Handlungsempfehlungen (Haiku 4.5).
              Ohne Datumsangabe wird automatisch der Zeitraum „ab erstem Umsatz, max. 6 Monate" verwendet.
            </p>

            <div className="datev-period-row" style={{ marginBottom: '1rem' }}>
              <label>
                <span>Von (optional)</span>
                <input
                  type="date"
                  className="form-input"
                  value={reportFrom}
                  onChange={(e) => setReportFrom(e.target.value)}
                />
              </label>
              <label>
                <span>Bis (optional)</span>
                <input
                  type="date"
                  className="form-input"
                  value={reportTo}
                  onChange={(e) => setReportTo(e.target.value)}
                />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '1.6rem' }}>
                <input
                  type="checkbox"
                  checked={reportIncludeBrief}
                  onChange={(e) => setReportIncludeBrief(e.target.checked)}
                />
                <Sparkles size={14} /> KI-Brief beilegen
              </label>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => { setReportFrom(''); setReportTo(''); }}
                disabled={reportPreviewLoading}
              >
                Auto-Zeitraum
              </button>
            </div>

            {reportPreviewLoading ? (
              <LoadingSpinner />
            ) : reportPreview ? (
              <>
                <div className="datev-summary" style={{ marginBottom: '1rem' }}>
                  <div className="datev-kpi">
                    <div className="label">Zeitraum</div>
                    <div className="value">
                      {reportPreview.period.start} – {reportPreview.period.end}
                    </div>
                  </div>
                  <div className="datev-kpi">
                    <div className="label">Rechnungen</div>
                    <div className="value">{reportPreview.counts.invoices}</div>
                  </div>
                  <div className="datev-kpi">
                    <div className="label">Ausgaben</div>
                    <div className="value">{reportPreview.counts.expenses}</div>
                  </div>
                  <div className="datev-kpi">
                    <div className="label">Kunden</div>
                    <div className="value">{reportPreview.counts.customers}</div>
                  </div>
                </div>

                {reportPreview.anomalies && reportPreview.anomalies.length > 0 && (
                  <div style={{
                    background: '#fef3c7',
                    border: '1px solid #f59e0b',
                    borderRadius: 8,
                    padding: '0.75rem 1rem',
                    marginBottom: '1rem',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.4rem', fontWeight: 600, color: '#92400e' }}>
                      <AlertTriangle size={16} /> Auffälligkeiten ({reportPreview.anomalies.length})
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#78350f', fontSize: '0.88rem' }}>
                      {reportPreview.anomalies.map((a, i) => (
                        <li key={i}>{a.msg}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <button
                  className="btn btn-primary"
                  onClick={downloadFullReport}
                  disabled={reportDownloading}
                >
                  <Download size={16} />
                  {reportDownloading ? 'Wird erstellt…' : 'Vollständigen Bericht (ZIP) herunterladen'}
                </button>
              </>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#6b7280', fontSize: '0.88rem' }}>
                <Info size={14} /> Vorschau wird geladen…
              </div>
            )}
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-header"><h2>Zeitraum</h2></div>
          <div className="card-body">
            <div className="datev-period-row">
              <label className="datev-kind">
                <span>Typ</span>
                <select className="form-input" value={kind} onChange={(e) => setKind(e.target.value)}>
                  <option value="month">Monat</option>
                  <option value="quarter">Quartal</option>
                  <option value="year">Jahr</option>
                  <option value="custom">Benutzerdefiniert</option>
                </select>
              </label>

              {kind !== 'custom' && (
                <label>
                  <span>Jahr</span>
                  <select className="form-input" value={year} onChange={(e) => setYear(Number(e.target.value))}>
                    {years.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </label>
              )}

              {kind === 'month' && (
                <label>
                  <span>Monat</span>
                  <select className="form-input" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                    {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                  </select>
                </label>
              )}

              {kind === 'quarter' && (
                <label>
                  <span>Quartal</span>
                  <select className="form-input" value={quarter} onChange={(e) => setQuarter(Number(e.target.value))}>
                    <option value={1}>Q1 (Jan – Mär)</option>
                    <option value={2}>Q2 (Apr – Jun)</option>
                    <option value={3}>Q3 (Jul – Sep)</option>
                    <option value={4}>Q4 (Okt – Dez)</option>
                  </select>
                </label>
              )}

              {kind === 'custom' && (
                <>
                  <label>
                    <span>Von</span>
                    <input type="date" className="form-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                  </label>
                  <label>
                    <span>Bis</span>
                    <input type="date" className="form-input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                  </label>
                </>
              )}
            </div>

            <label className="datev-toggle">
              <input
                type="checkbox"
                checked={includeUnpaid}
                onChange={(e) => setIncludeUnpaid(e.target.checked)}
              />
              <span>
                Offene Rechnungen einbeziehen
                <small>
                  Standard: nur <b>bezahlte</b> Rechnungen. Wenn aktiviert, werden auch offene
                  Rechnungen (versendet / überfällig / Entwurf) als <b>[OFFEN]</b> markiert exportiert.
                </small>
              </span>
            </label>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8"><LoadingSpinner size="large" /></div>
        ) : preview ? (
          <>
            <div className="datev-summary">
              <div className="datev-kpi">
                <div className="label">Zeitraum</div>
                <div className="value">
                  {preview.period.start_date} – {preview.period.end_date}
                </div>
              </div>
              <div className="datev-kpi">
                <div className="label">Bezahlt</div>
                <div className="value ok">
                  {preview.summary.count_paid} · {formatEuro(preview.summary.total_paid)}
                </div>
              </div>
              {preview.include_unpaid && (
                <div className="datev-kpi">
                  <div className="label">Offen</div>
                  <div className="value warn">
                    {preview.summary.count_open} · {formatEuro(preview.summary.total_open)}
                  </div>
                </div>
              )}
              <div className="datev-kpi total">
                <div className="label">Gesamt</div>
                <div className="value">
                  {preview.summary.count} · {formatEuro(preview.summary.total)}
                </div>
              </div>
            </div>

            <div className="flex gap-2 mb-4">
              <button
                className="btn btn-primary"
                disabled={!preview.summary.count || downloading !== null}
                onClick={() => handleDownload('datev')}
              >
                <Download size={16} />
                {downloading === 'datev' ? 'Wird erstellt…' : 'DATEV Export'}
              </button>
              <button
                className="btn btn-outline"
                disabled={!preview.summary.count || downloading !== null}
                onClick={() => handleDownload('simple')}
              >
                <FileSpreadsheet size={16} />
                {downloading === 'simple' ? 'Wird erstellt…' : 'Einfache CSV'}
              </button>
            </div>

            <div className="card">
              <div className="card-header"><h2>Vorschau</h2></div>
              <div className="card-body">
                {preview.rows.length === 0 ? (
                  <div className="text-center py-8 text-gray-600">
                    Keine Rechnungen im gewählten Zeitraum.
                    {!includeUnpaid && (
                      <div className="mt-2 text-sm">
                        Tipp: „Offene Rechnungen einbeziehen" aktivieren, um unbezahlte zu sehen.
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Rechnungsnr.</th>
                          <th>Datum</th>
                          <th>Kunde</th>
                          <th>Leistung</th>
                          <th className="text-right">Betrag</th>
                          <th>Status</th>
                          <th>Im Export</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.rows.map((r) => (
                          <tr key={r.invoice_number}>
                            <td className="font-semibold">{r.invoice_number}</td>
                            <td>{r.invoice_date}</td>
                            <td>{r.customer_name}</td>
                            <td className="truncate" title={r.title}>{r.title}</td>
                            <td className="text-right">{formatEuro(r.net_amount)}</td>
                            <td>
                              <span className={STATUS_CLASS[r.status] || 'status-badge'}>
                                {r.status_label}
                              </span>
                            </td>
                            <td>
                              {r.is_paid ? (
                                <span className="export-flag ok">
                                  <CheckCircle2 size={14} /> Bezahlt
                                </span>
                              ) : (
                                <span className="export-flag warn">
                                  <AlertCircle size={14} /> [OFFEN]
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default DatevExport;
